import React, {Component} from "react";
import PropTypes from "prop-types";
import _uniqueId from "lodash/uniqueId";
import Button from "../button/Button";
import {base64ToBlob, downloadBlob} from "../../utils/FileDownloader";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import "./FileUploader.css";
import Tooltip from "../tooltip/Tooltip";
import TimesIcon from "../action-menu/icons/TimesIcon";

export default class FileUploader extends Component {
    static defaultProps = {
        id: null,
        value: null,
        multiple: false,
        accept: null,
        specificFileTypes: [],
        disabled: false,
        hidden: false,
        maxFileSize: null,
        invalidFileSizeMessage: null,
        invalidFileTypeMessage: null,
        style: null,
        className: "",
        chooseLabel: "Choose",
        noFileLabel: "No Selected File/s...",
        onChange: null,
        uploadHandler: null,
        basic: false
    };

    static propTypes = {
        id: PropTypes.string,
        value: PropTypes.oneOfType([PropTypes.object, PropTypes.array]),
        multiple: PropTypes.bool,
        accept: PropTypes.string,
        specificFileTypes: PropTypes.array,
        disabled: PropTypes.bool,
        hidden: PropTypes.bool,
        maxFileSize: PropTypes.number,
        invalidFileSizeMessage: PropTypes.string,
        invalidFileTypeMessage: PropTypes.string,
        style: PropTypes.object,
        className: PropTypes.string,
        chooseLabel: PropTypes.string,
        noFileLabel: PropTypes.string,
        onChange: PropTypes.func.isRequired,
        uploadHandler: PropTypes.func,
        basic: PropTypes.bool
    };

    constructor(props) {
        super(props);
        if (props.id) {
            this.id = props.id;
        } else {
            this.id = _uniqueId("dim14-fileUpload-id-");
        }
        this.state = {
            errors: []
        };
        this.inputRef = React.createRef();
    }

    async addItem(event) {
        if (!this.props.uploadHandler) {
            let tempFiles = [];
            // If file input is multiple, then keep previously selected files
            // and keep adding to them
            if (this.props.multiple && this.props.value) {
                if (this.props.value.constructor === Array) {
                    tempFiles = this.props.value;
                } else {
                    tempFiles.push(this.props.value);
                }
            }
            for (let i = 0; i < event.target.files.length; i++) {
                event.persist();
                let file = event.target.files[i];
                let filetype = file.type || 'application/octet-stream';
                let name = file.name || 'file';
                let filesize = file.size;
                let specificType;
                if (name.lastIndexOf(".") > 0) {
                    specificType = name.substr(name.lastIndexOf(".") + 1);
                }
                /**
                 * If you want to do very strong file type checking, then you can use file headers.
                 * Either convert HEX values of specific file types and check if they exist in base64String of files
                 * or convert base64String of files to HEX, and check if they match with given HEX values of file types
                 */
                if ((this.props.maxFileSize && (filesize > this.props.maxFileSize)) || (filesize <= 0)) {
                    let temp = this.state.errors;
                    let message = "Maximum allowed upload size is " + this.props.maxFileSize + " bytes. (Size of " + name + " is " + filesize + " bytes)";
                    if (this.props.invalidFileSizeMessage) {
                        message = this.props.invalidFileSizeMessage;
                    }
                    temp.push(message);
                    this.setState({errors: temp});
                } else {
                    // If string of file types are given in a array, checks whether file type matches with any of them.
                    // if there is no file extansion, then accept the file
                    if ((this.props.specificFileTypes.length > 0) && specificType && !this.props.specificFileTypes.includes(specificType.toLowerCase())) {
                        let temp = this.state.errors;
                        let message = "You can only upload files with extension " + this.props.specificFileTypes + ". (File you upload has extension of " + specificType + ")";
                        if (this.props.invalidFileTypeMessage) {
                            message = this.props.invalidFileTypeMessage;
                        }
                        temp.push(message);
                        this.setState({errors: temp});
                    } else {
                        let reader = new FileReader();
                        await new Promise((resolve) => {
                            reader.readAsDataURL(file);
                            reader.onloadend = (evt) => {
                                let dataAsDataUrl = evt.target.result;
                                let base64String = dataAsDataUrl.replace(/^data:.+(;base64)?,/, '');
                                let processedFile = {
                                    content: base64String,
                                    mimeType: filetype,
                                    name: name,
                                    fileSize: filesize
                                };
                                // Check if newly loaded file already exists in the list, if not add it
                                if (tempFiles.every(item => {
                                    return (item.content !== processedFile.content);
                                })) {
                                    tempFiles.push(processedFile);
                                }
                                resolve();
                            };
                        });
                    }
                }
            }
            if (tempFiles.length === 1) {
                this.props.onChange(tempFiles[0], event.target.files[0]);
            } else {
                if (tempFiles.length === 0) {
                    this.props.onChange(null, null);
                } else {
                    this.props.onChange(tempFiles, event.target.files);
                }
            }
        } else {
            this.props.uploadHandler(event);
        }
        // If user wants to select last selected file again,
        // he/she can do it since we clean the file input's value
        this.inputRef.current.value = "";
    };

    fileDelete(item) {
        if (this.props.value.constructor === Array) {
            let temp = this.props.value;
            let indexToDelete = temp.findIndex(elem => item.content === elem.content);
            temp.splice(indexToDelete, 1);
            if (temp.length === 0) {
                this.props.onChange(null);
            } else {
                this.props.onChange(temp);
            }
        } else {
            this.props.onChange(null);
        }
    }

    getFileArray() {
        if (!this.props.value) {
            return [];
        } else if (this.props.value.constructor === Array && this.props.value.length > 0) {
            return this.props.value;
        } else {
            return [this.props.value];
        }
    }

    selectedFiles() {
        if (this.props.value) {
            let tempValue = this.getFileArray();
            let temp = [];
            tempValue.forEach((item, index) => {
                let preview;
                let fileURL = URL.createObjectURL(base64ToBlob(item.content, item.mimeType));
                if (item.mimeType.includes("image")) {
                    preview = <div className="imagePreviewDiv"><img className="imagePreview" src={fileURL} alt="preview"
                                                                    onClick={() => window.open(fileURL)}/></div>
                } else {
                    preview = <span style={{display: "unset", margin: "50px"}}/>
                }
                temp.push(<li key={index} className="selectedLI">
                    {preview}
                    <div className="labelDiv"><label className="selectedLabel"
                                                     onClick={() => downloadBlob(base64ToBlob(item.content, item.mimeType), item.name)}>{item.name}</label>
                    </div>
                    <div className="closeDiv"><FontAwesomeIcon className="icon" onClick={() => this.fileDelete(item)}
                                                               icon={["fas", "times"]}/></div>
                </li>);
            });
            return temp;
        } else {
            return <li className="noSelectionLabel">{this.props.noFileLabel}</li>
        }
    }

    clearAll() {
        this.props.onChange(this.props.value?.constructor === Array ? [] : null);
    }

    clearAllButton() {
        return <TimesIcon className="m-1" onClick={() => this.clearAll()}/>
    }

    fileInput() {
        return (
            <input type="file"
                   multiple={this.props.multiple}
                   accept={this.props.accept}
                   hidden={true}
                   onChange={event => this.addItem(event)}
                   ref={this.inputRef}/>
        );
    }

    chooseButton() {
        return (
            <Button
                disabled={this.props.disabled}
                onClick={() => this.inputRef.current.click()}
            >
                <FontAwesomeIcon style={{float: "left"}} icon={["fas", "plus"]}/>
                {this.props.chooseLabel}
            </Button>
        );
    }

    errorMessages() {
        if (this.state.errors.length > 0) {
            let temp = [];
            for (let i = 0; i < this.state.errors.length; i++) {
                temp.push(<li key={i}>{this.state.errors[i]}</li>);
            }
            return <ul style={{color: "#ff0000"}}>
                {temp}
            </ul>
        } else {
            return null;
        }
    }

    errorMessageCleaner() {
        if (this.state.errors.length > 0) {
            this.setState({errors: []});
        }
    }

    render() {
        let componentClassName = "Dim14FileUploader " + this.props.className;
        const files = this.getFileArray();

        if (!this.props.hidden) {
            return (
                this.props.basic ?
                    <div id={this.id} style={this.props.style} className="d-flex align-items-center">
                        <Tooltip title={this.errorMessages() ?? ""}>
                            {files.map(f => f.name).join(', ')}
                        </Tooltip>
                        {this.fileInput()}
                        {files.length > 0 ? this.clearAllButton() : this.chooseButton()}
                    </div>
                    :
                    <div id={this.id} className={componentClassName} style={this.props.style}
                         onClick={() => this.errorMessageCleaner()}>
                        <ul className="selectedUL">
                            {this.selectedFiles()}
                        </ul>
                        {this.fileInput()}
                        {this.chooseButton()}
                        {this.errorMessages()}
                    </div>
            );
        } else {
            return null;
        }
    }
}
